/**
 * true - uses user's locale
 * false - uses en-US
 * string - uses that given locale
 */
type UseLocale = boolean | string;

type ShortenNumberOptions = {
  /**
   * @default true
   *
   * true - uses user's locale
   * false - uses en-US
   * string - uses that given locale
   */
  useLocale?: UseLocale;

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
  minimumIntegerDigits?: number;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  minimumSignificantDigits?: number;
  maximumSignificantDigits?: number;

  shortenFromNumber?: number;
};

type Scale = 'k' | 'M' | 'B' | null;

const defaultShortenNumberOptions = {
  useLocale: true,
  shortenFromNumber: 1000,
};

const toFixedNoRounding = (number, digits) => {
  // Inspired from here https://stackoverflow.com/a/9232092
  const truncateFn = Math[number < 0 ? 'ceil' : 'floor'];

  /**
   * The ceil and floor functions always round to the next integer.
   * This is the reason why we multiply it by 10 * the number of digits to trunca
   *
   * So  we want to truncate 5.467 to 2 decimal places, which is 5.46.
   * We do 5.467 * 10 * 2 = 546.7.  Math.floor(546.7) = 546.
   * We then restore the original decimal places with division.
   * 546 /(10*2) = 5.46.
   * */
  return truncateFn(number * 10 * digits) / (10 * digits);
};

const getNumberToFormatNoRounding = (numberToBeFormatted, options) => {
  const numberOfFractionalDigits =
    String(numberToBeFormatted).split('.')?.[1]?.length;

  if (numberOfFractionalDigits > options.maximumFractionDigits) {
    return toFixedNoRounding(
      numberToBeFormatted,
      options.maximumFractionDigits,
    );
  }

  return numberToBeFormatted;
};

const scales = [
  {
    unit: 'B',
    value: 1000000000,
    options: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
  },
  {
    unit: 'M',
    value: 1000000,
    options: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
  },
  {
    unit: 'k',
    value: 1000,
    options: {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
  },
] as const;

export function shortenNumber(
  number: number,
  options: ShortenNumberOptions = {},
): { number: number; scale: Scale; formatted: string } {
  const { useLocale, shortenFromNumber, ...otherOptions } = {
    ...defaultShortenNumberOptions,
    ...options,
  };

  // ? ts workaround for userLanguage
  const locale =
    typeof useLocale === 'string'
      ? useLocale
      : navigator['userLanguage'] || navigator.language;

  const numberAdjusted = Math.abs(number);

  const scale = scales.find(
    (scale) => numberAdjusted >= scale.value,
    // scale.value - numberAdjusted <= (scale.value * 4) / 10,
  );

  const formatterOptions: Intl.NumberFormatOptions = {
    ...(scale ? scale.options : {}),
    ...otherOptions,
    style: 'decimal',
  };

  const formatter = new Intl.NumberFormat(
    useLocale ? locale : 'en-US',
    formatterOptions,
  );

  if (!scale || numberAdjusted < shortenFromNumber) {
    return {
      number,
      scale: null,
      formatted: formatter.format(
        // Avoid inherent rounding in Intl.NumberFormat
        getNumberToFormatNoRounding(number, formatterOptions),
      ),
    };
  }

  const numberShortened = number / scale.value;

  return {
    number: numberShortened,
    scale: scale.unit,
    formatted: `${formatter.format(numberShortened)}${scale.unit}`,
  };
}

type FormatNumberOptions = {
  /**
   * @default true
   */
  useLocale?: UseLocale;
} & Intl.NumberFormatOptions;

export function formatNumber(
  number: number | null | undefined | string,
  options: FormatNumberOptions = {},
): string {
  if (number === undefined || number === null || Number.isNaN(number)) {
    return '';
  }
  const { useLocale = true, ...otherOptions } = options;

  const locale =
    typeof useLocale === 'string'
      ? useLocale
      : navigator['userLanguage'] || navigator.language;

  return new Intl.NumberFormat(
    useLocale ? locale : 'en-US',
    otherOptions,
  ).format(Number(number));
}

export function formatPercentage(
  number: number | null | undefined | string,
  options: FormatNumberOptions = {},
): string {
  const formatted = formatNumber(number, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  });

  return formatted === '' ? '' : `${formatted}%`;
}

const SYMBOLS_AS_HUMAN_READABLE = {
  '.': 'dot',
  ',': 'comma',
  ' ': 'space',
  ' ': 'space', // no break space chacater here
};

type NumberFormattingOptions = {
  /**
   * @default true
   *
   * true - uses user's locale
   * false - uses en-US
   * string - uses that given locale
   */
  useLocale?: boolean | string;

  /**
   * @default false
   *
   * Whether to return the response as human readable strings rather than symbols
   */
  asHumanReadable?: boolean;
};

export function getNumberFormattingOptions(
  options: NumberFormattingOptions = {},
): {
  thousandsSeparator: string;
  decimalSeparator: string;
} {
  const { useLocale = true, asHumanReadable = false } = options;

  const locale =
    typeof useLocale === 'string'
      ? useLocale
      : navigator['userLanguage'] || navigator.language;

  const formatted = new Intl.NumberFormat(useLocale ? locale : 'en-US', {
    style: 'decimal',
  })
    .format(12345.12)
    .replace(/[0-9]/g, '');

  return {
    thousandsSeparator:
      asHumanReadable && SYMBOLS_AS_HUMAN_READABLE[formatted[0]]
        ? SYMBOLS_AS_HUMAN_READABLE[formatted[0]]
        : formatted[0],
    decimalSeparator:
      asHumanReadable && SYMBOLS_AS_HUMAN_READABLE[formatted[1]]
        ? SYMBOLS_AS_HUMAN_READABLE[formatted[1]]
        : formatted[1],
  };
}

type ParseNumberOptions = {
  /**
   * @default true
   *
   * true - uses user's locale
   * false - uses en-US
   * string - uses that given locale
   */
  useLocale?: boolean | string;

  /**
   * @default false
   */
  allowTrailingDecimalSeparator?: boolean;

  /**
   * @default true
   */
  allowLeadingDecimalSeparator?: boolean;
};

/**
 * @returns numeric value representation of the value given
 */
export function parseNumber(
  value: unknown,
  options: ParseNumberOptions = {},
): number | null {
  if (value === null || typeof value === 'undefined' || value === '') {
    return null;
  }

  if (typeof value !== 'string') {
    console.error(`parseNumber can only be used with string input`);
    return null;
  }

  const {
    useLocale = true,
    allowTrailingDecimalSeparator = false,
    allowLeadingDecimalSeparator = true,
  } = options;

  const { thousandsSeparator, decimalSeparator } = getNumberFormattingOptions({
    useLocale,
  });

  let valueCleaned = value
    .trim()
    .replace(/ /g, '') // If spaces are present, they always serve as thousands separators or extra characters
    .replaceAll(thousandsSeparator, '') // Removes thousands separators
    .replaceAll(decimalSeparator, '.') // Replace decimal separator with `.`
    .replace(/[^0-9.e]/g, ''); // Removes any non digit or dot characters

  if (valueCleaned.endsWith('.')) {
    if (allowTrailingDecimalSeparator) {
      valueCleaned = valueCleaned.slice(0, -1);
    } else {
      return null;
    }
  }

  if (valueCleaned.startsWith('.')) {
    if (allowLeadingDecimalSeparator) {
      valueCleaned = `0${valueCleaned}`;
    } else {
      return null;
    }
  }

  const parsed = parseFloat(valueCleaned);

  return Number.isNaN(parsed) ? null : parsed;
}

export function isValidNumberInput(
  value: unknown,
  options: ParseNumberOptions = {},
): boolean {
  const {
    useLocale = true,
    allowTrailingDecimalSeparator = false,
    allowLeadingDecimalSeparator = true,
  } = options;

  // Intentionally treating empty string as valid number input
  if (value === null || typeof value === 'undefined' || value === '') {
    return true;
  }

  if (typeof value !== 'string') {
    console.error(`isValidNumberInput can only be used with string input`);
    return false;
  }

  const { thousandsSeparator, decimalSeparator } = getNumberFormattingOptions({
    useLocale,
  });

  // Intentionally including dot, comma and space as allowed at this stage
  // Intentionally NOT allowing scientific notation `e`
  // eslint-disable-next-line no-useless-escape
  const unallowedCharactersPattern = `[^0-9\\${thousandsSeparator}\\${decimalSeparator}\ \.\,]`;
  const unallowedCharacters = new RegExp(unallowedCharactersPattern, 'g');

  if (unallowedCharacters.test(value)) {
    return false;
  }

  let valueTrimmed = value.replace(unallowedCharacters, '').trim();

  // Handle trailing decimal separator
  // if trailing is allowed, we adjust the value to not contain it for further parsing
  if (
    valueTrimmed.endsWith(decimalSeparator) &&
    allowTrailingDecimalSeparator
  ) {
    valueTrimmed = valueTrimmed.slice(0, decimalSeparator.length * -1);
  }

  // Handle leading decimal separator
  // if leading is allowed, we adjust the value to start with `0` for further parsing
  if (
    valueTrimmed.startsWith(decimalSeparator) &&
    allowLeadingDecimalSeparator
  ) {
    valueTrimmed = `0${valueTrimmed}`;
  }

  // Input must start and end with digit at this point to be valid
  if (
    !/^[0-9]+$/.test(valueTrimmed[0]) ||
    !/^[0-9]+$/.test(valueTrimmed[valueTrimmed.length - 1])
  ) {
    return false;
  }

  const [integer, decimal, ...others] = valueTrimmed.split(decimalSeparator);

  // If multiple decimal separators were present, input is incorrect
  if (others && others.length > 0) {
    return false;
  }

  // Either no decimal part or just digits present
  const isValidDecimal = !decimal || /^[0-9]+$/.test(decimal);

  // If spaces are present, they always serve as thousands separators or extra characters
  const adjustedInteger = integer.replace(/ /g, '');

  const integerParts = adjustedInteger.split(thousandsSeparator);

  // Either one big digits-only part or each part must be digits-only
  const isValidInteger =
    integerParts.length === 1
      ? /^[0-9]+$/.test(integerParts[0])
      : integerParts.every((int, i) => {
          if (i === 0) {
            return int.length < 4 && int.length > 0 && /^[0-9]+$/.test(int);
          }
          return int.length === 3 && /^[0-9]+$/.test(int);
        });

  return isValidDecimal && isValidInteger;
}

const THOUSAND = 1_000;
const MILLION = 1_000_000;
const BILLION = 1_000_000_000;

export const formatNumberWithMagnitudeSuffix = (value: number): string => {
  if (value < THOUSAND) {
    return value.toString();
  }

  let divisor: number;
  let suffix: 'k' | 'm' | 'b';

  if (value >= BILLION) {
    divisor = BILLION;
    suffix = 'b';
  } else if (value >= MILLION) {
    divisor = MILLION;
    suffix = 'm';
  } else {
    divisor = THOUSAND;
    suffix = 'k';
  }

  const formatted = (value / divisor).toFixed(1);

  return formatted.replace(/\.?0+$/, '') + suffix;
};

/**
 * Checks if a numeric value is negative
 * @param value The value to check - can be a number, numeric string, or undefined
 * @returns true if the value is a negative number, false otherwise
 */
export function isNegativeAmount(
  value: string | number | null | undefined,
): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  return Number(value) < 0;
}
